import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { LoadingButton } from '@mui/lab'
import { Box, Button, Divider, FormHelperText, Grid, IconButton, Stack, Typography, styled } from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

import TextInputController from '@/components/ui/inputs/TextInputController'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import CheckboxInput from '@/components/ui/inputs/CheckboxInput'

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}))

const policyFormSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  permissions: z
    .array(
      z.object({
        action: z.array(z.string()).min(1),
        resource: z.array(z.string()).min(1),
      })
    )
    .min(1),
})

type PolicyFormValues = z.infer<typeof policyFormSchema>

const actions = ['create', 'read', 'update', 'delete']

export type PolicyFormProps = {
  initialValues?: PolicyFormValues
  isSubmitting?: boolean
  onSubmit: (values: PolicyFormValues) => void
  readOnly?: boolean
}

export function PolicyForm(props: PolicyFormProps) {
  const { initialValues, isSubmitting = false, onSubmit: onSubmitProp, readOnly = false } = props
  const { t } = useTranslation()

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<PolicyFormValues>({
    defaultValues: initialValues ?? { name: '', description: '', permissions: [{ action: [], resource: [] }] },
    resolver: zodResolver(policyFormSchema),
    mode: readOnly ? 'onSubmit' : 'onBlur',
  })
  const { fields: permissionFields, append, remove } = useFieldArray({ control, name: 'permissions' })

  const resources = useMemo(
    () => [
      { label: 'Admin', name: 'Admin' },
      { label: 'Custom device', name: 'CustomDevice' },
      { label: 'Consultation', name: 'Consultation' },
      { label: 'Establishment', name: 'Establishment' },
      { label: 'Expertise', name: 'Expertise' },
      { label: 'Meeting link', name: 'MeetingLink' },
      { label: 'Patient', name: 'Patient' },
      { label: 'Staff', name: 'Staff' },
      { label: 'Statistics', name: 'Statistics' },
    ],
    []
  )

  const onSubmit: SubmitHandler<PolicyFormValues> = (values) => {
    onSubmitProp(values)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container rowGap={3} sx={{ mt: '0' }}>
        <Grid item xs={12} md={6}>
          <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <TextInputController
            control={control}
            fullWidth
            label={t('field.description.label')}
            name="description"
            multiline
            minRows={3}
            type="textarea"
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Typography>{t('field.permissions.label')}</Typography>
            <Box>
              {permissionFields.map((field, index) => (
                <Accordion key={field.id}>
                  <AccordionSummary sx={{ display: 'flex' }}>
                    <Typography>{field.resource.length && field.action.length ? `${field.resource.join(', ')}: ${field.action.join(', ')}` : ''}</Typography>
                    <Stack direction="row" flex={1} justifyContent="end">
                      {!readOnly && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            remove(index)
                          }}
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    <Stack divider={<Divider />}>
                      <Box sx={{ padding: 2 }}>
                        <Grid container columnGap={3} wrap="nowrap">
                          <Grid item xs={3}>
                            <Typography
                              variant="h6"
                              sx={{ color: colors.gray900, fontSize: '18px', fontWeight: 500, lineHeight: '24px', mt: '6px', pl: '16px' }}
                            >
                              Actions
                            </Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <Controller
                              control={control}
                              name={`permissions.${index}.action`}
                              render={({ field }) => (
                                <Stack direction="column" spacing={1}>
                                  <RadioGroupInput
                                    disabled={readOnly}
                                    row
                                    onChange={(_event, value) => {
                                      setValue(field.name, value === 'manage' ? [value] : [])
                                    }}
                                    options={[
                                      { label: 'Specific', value: '' },
                                      { label: 'All actions', value: 'manage' },
                                    ]}
                                    value={field.value?.includes('manage') ? 'manage' : ''}
                                    sx={{
                                      '& .MuiRadio-root.Mui-disabled, & .MuiFormControlLabel-label.Mui-disabled': {
                                        color: '#002E3F',
                                      },
                                    }}
                                  />
                                  {!field.value?.includes('manage') && (
                                    <Stack direction="column">
                                      {actions.map((action) => (
                                        <CheckboxInput
                                          key={`${field.name}-${action}`}
                                          checked={field.value.includes(action)}
                                          label={action}
                                          onChange={() => {
                                            setValue(
                                              field.name,
                                              field.value.includes(action) ? field.value?.filter((v) => v !== action) : field.value?.concat(action)
                                            )
                                          }}
                                          readOnly={readOnly}
                                        />
                                      ))}
                                    </Stack>
                                  )}
                                  {errors.permissions?.[index]?.action && (
                                    <FormHelperText error sx={{ position: 'initial' }}>
                                      {errors.permissions[index]?.action?.message}
                                    </FormHelperText>
                                  )}
                                </Stack>
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box sx={{ padding: 2 }}>
                        <Grid container columnGap={3} wrap="nowrap">
                          <Grid item xs={3}>
                            <Typography
                              variant="h6"
                              sx={{ color: colors.gray900, fontSize: '18px', fontWeight: 500, lineHeight: '24px', mt: '6px', pl: '16px' }}
                            >
                              Resource
                            </Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <Controller
                              control={control}
                              name={`permissions.${index}.resource`}
                              render={({ field }) => (
                                <Stack direction="column" spacing={1}>
                                  <RadioGroupInput
                                    disabled={readOnly}
                                    row
                                    onChange={(_event, value) => {
                                      setValue(field.name, value === 'all' ? resources.map((resource) => resource.name) : [])
                                    }}
                                    options={[
                                      { label: 'Specific', value: '' },
                                      { label: 'All resources', value: 'all' },
                                    ]}
                                    value={resources.every((resource) => !!field.value?.find((v) => resource.name === v)) ? 'all' : ''}
                                    sx={{
                                      '& .MuiRadio-root.Mui-disabled, & .MuiFormControlLabel-label.Mui-disabled': {
                                        color: '#002E3F',
                                      },
                                    }}
                                  />
                                  {!resources.every((resource) => !!field.value?.find((v) => resource.name === v)) && (
                                    <Stack direction="column">
                                      {resources.map((resource) => {
                                        return (
                                          <CheckboxInput
                                            key={resource.name}
                                            checked={field.value.includes(resource.name)}
                                            label={resource.label}
                                            onChange={() => {
                                              setValue(
                                                field.name,
                                                field.value.includes(resource.name)
                                                  ? field.value?.filter((v) => v !== resource.name)
                                                  : field.value?.concat(resource.name)
                                              )
                                            }}
                                            readOnly={readOnly}
                                          />
                                        )
                                      })}
                                    </Stack>
                                  )}
                                  {errors.permissions?.[index]?.resource && (
                                    <FormHelperText error sx={{ position: 'initial' }}>
                                      {errors.permissions[index]?.resource?.message}
                                    </FormHelperText>
                                  )}
                                </Stack>
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
            <Box>
              {!readOnly && (
                <Button color="primary" onClick={() => append({ action: [], resource: [] })}>
                  Add permission
                </Button>
              )}
            </Box>
          </Stack>
        </Grid>
        <Grid container item xs={12} justifyContent="end">
          {!readOnly && (
            <LoadingButton loading={isSubmitting} type="submit" variant="contained">
              {t('common:button.submit.label')}
            </LoadingButton>
          )}
        </Grid>
      </Grid>
    </form>
  )
}

export default PolicyForm
